
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  ResponsiveContainer 
} from 'recharts';
import { ChartDetailModal } from './ChartDetailModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Filter, Calendar, Download, RefreshCw } from 'lucide-react';
import { TimeRangeFilter } from './TimeRangeFilter';
import { TagFilterComponent } from './TagFilterComponent';

interface ChartData {
  name: string;
  value: number;
  category?: string;
  unit?: string;
  source?: string;
  status?: string;
  date?: string;
  breakdown?: Record<string, number>;
  scope?: string;
  calculation_type?: string;
  activity_name?: string;
}

interface ChartFilters {
  timeRange: string;
  scope: string;
  category: string;
  calculationType: string;
  searchTerm: string;
  selectedTags: string[];
}

export const EnhancedDashboardCharts = () => {
  const [selectedChart, setSelectedChart] = useState<{
    title: string;
    data: ChartData[];
    category: string;
    clickedValue?: number;
    clickedLabel?: string;
  } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chartData, setChartData] = useState({
    emissions: [] as ChartData[],
    energy: [] as ChartData[],
    esgScores: [] as ChartData[],
    monthlyTrends: [] as ChartData[]
  });
  const [rawData, setRawData] = useState({
    carbonData: [] as any[],
    esgData: [] as any[],
    targetsData: [] as any[]
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ChartFilters>({
    timeRange: '1year',
    scope: 'all',
    category: 'all',
    calculationType: 'all',
    searchTerm: '',
    selectedTags: []
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  useEffect(() => {
    if (rawData.carbonData.length > 0 || rawData.esgData.length > 0) {
      processChartData();
    }
  }, [rawData, filters]);

  const fetchUserData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on filter
      const endDate = new Date();
      const startDate = new Date();
      switch (filters.timeRange) {
        case '3months':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case '2years':
          startDate.setFullYear(endDate.getFullYear() - 2);
          break;
        case '3years':
          startDate.setFullYear(endDate.getFullYear() - 3);
          break;
        case '5years':
          startDate.setFullYear(endDate.getFullYear() - 5);
          break;
        default:
          startDate.setFullYear(2020); // Default to 2020 for 'all'
      }

      // Fetch user's carbon calculations with filters
      let carbonQuery = supabase
        .from('carbon_calculations')
        .select('*')
        .eq('user_id', user?.id)
        .gte('calculation_date', startDate.toISOString().split('T')[0])
        .lte('calculation_date', endDate.toISOString().split('T')[0])
        .order('created_at', { ascending: false });

      if (filters.scope !== 'all') {
        carbonQuery = carbonQuery.eq('scope', filters.scope);
      }

      if (filters.calculationType !== 'all') {
        carbonQuery = carbonQuery.eq('calculation_type', filters.calculationType);
      }

      const { data: carbonData } = await carbonQuery;

      // Fetch user's ESG data points from unified library with filters
      let esgQuery = supabase
        .from('user_metrics_data')
        .select(`
          *,
          metric:unified_metrics_library(*)
        `)
        .eq('organization_id', user?.id)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (filters.category !== 'all') {
        esgQuery = esgQuery.eq('category', filters.category);
      }

      const { data: esgData } = await esgQuery;

      // Fetch user's net-zero targets for progress tracking
      const { data: targetsData } = await supabase
        .from('net_zero_targets')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      setRawData({
        carbonData: carbonData || [],
        esgData: esgData || [],
        targetsData: targetsData || []
      });

    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = () => {
    // Filter data based on search term and tags
    let filteredCarbonData = filters.searchTerm 
      ? rawData.carbonData.filter(item => 
          item.activity_name?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          item.calculation_type?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          item.scope?.toLowerCase().includes(filters.searchTerm.toLowerCase())
        )
      : rawData.carbonData;

    let filteredEsgData = filters.searchTerm
      ? rawData.esgData.filter(item =>
          item.name?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          item.category?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          item.subcategory?.toLowerCase().includes(filters.searchTerm.toLowerCase())
        )
      : rawData.esgData;

    // Apply tag filtering if tags are selected
    if (filters.selectedTags.length > 0) {
      // Import tag service for filtering
      import('@/services/tagService').then(({ tagService }) => {
        if (user) {
          // Filter carbon data by tags
          tagService.filterDataByTags(user.id, 'carbon', filteredCarbonData, filters.selectedTags, 'id')
            .then(taggedCarbonData => {
              filteredCarbonData = taggedCarbonData;
            });
          
          // Filter ESG data by tags
          tagService.filterDataByTags(user.id, 'esg', filteredEsgData, filters.selectedTags, 'id')
            .then(taggedEsgData => {
              filteredEsgData = taggedEsgData;
            });
        }
      });
    }

    // Process emissions data
    const emissionsData = filteredCarbonData.reduce((acc: Record<string, number>, item) => {
      const scope = item.scope || 'Other';
      acc[scope] = (acc[scope] || 0) + (item.co2_equivalent || 0);
      return acc;
    }, {});

    const processedEmissions = Object.entries(emissionsData).map(([scope, value]) => ({
      name: scope,
      value: Math.round(value as number),
      category: 'emissions',
      unit: 'tCO2e',
      source: 'User Carbon Calculations',
      status: 'verified',
      date: new Date().toISOString().split('T')[0],
      scope: scope,
      calculation_type: 'carbon',
      activity_name: scope
    }));

    // Process energy data
    const energyData = filteredCarbonData.filter(item => 
      item.calculation_type?.toLowerCase().includes('energy') || 
      item.activity_name?.toLowerCase().includes('energy') ||
      item.activity_name?.toLowerCase().includes('electricity')
    ).reduce((acc: Record<string, number>, item) => {
      const activity = item.activity_name || 'Other Energy';
      acc[activity] = (acc[activity] || 0) + (item.amount || 0);
      return acc;
    }, {});

    const processedEnergy = Object.entries(energyData).map(([activity, value]) => ({
      name: activity,
      value: Math.round(value as number),
      category: 'energy',
      unit: 'kWh',
      source: 'User Energy Data',
      status: 'verified',
      date: new Date().toISOString().split('T')[0],
      scope: 'Energy',
      calculation_type: 'energy',
      activity_name: activity
    }));

    // Process ESG scores
    const esgScores = filteredEsgData.reduce((acc: Record<string, { total: number, count: number }>, item) => {
      const category = item.category || 'General';
      if (!acc[category]) acc[category] = { total: 0, count: 0 };
      acc[category].total += item.custom_value || 0;
      acc[category].count += 1;
      return acc;
    }, {});

    const processedESG = Object.entries(esgScores).map(([category, { total, count }]) => ({
      name: category,
      value: Math.round(total / count),
      category: 'esg',
      unit: 'Score',
      source: 'User ESG Data',
      status: 'verified',
      date: new Date().toISOString().split('T')[0],
      scope: category,
      calculation_type: 'esg',
      activity_name: category
    }));

    // Process monthly trends
    const monthlyData = filteredCarbonData.reduce((acc: Record<string, number>, item) => {
      const month = new Date(item.calculation_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      acc[month] = (acc[month] || 0) + (item.co2_equivalent || 0);
      return acc;
    }, {});

    const processedMonthly = Object.entries(monthlyData).map(([month, value]) => ({
      name: month,
      value: Math.round(value as number),
      category: 'trends',
      unit: 'tCO2e',
      source: 'User Monthly Data',
      status: 'verified',
      date: new Date().toISOString().split('T')[0],
      scope: 'Monthly',
      calculation_type: 'trend',
      activity_name: month
    }));

    setChartData({
      emissions: processedEmissions,
      energy: processedEnergy,
      esgScores: processedESG,
      monthlyTrends: processedMonthly
    });
  };

  const handleChartClick = (data: any, chartInfo: { title: string; category: string; data: ChartData[] }) => {
    if (data && data.payload) {
      setSelectedChart({
        ...chartInfo,
        clickedValue: data.payload.value,
        clickedLabel: data.payload.name
      });
      setIsModalOpen(true);
    }
  };

  const handleFilterChange = (key: keyof ChartFilters, value: string | string[]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      timeRange: '1year',
      scope: 'all',
      category: 'all',
      calculationType: 'all',
      searchTerm: '',
      selectedTags: []
    });
  };

  const COLORS = ['#059669', '#0891b2', '#7c3aed', '#dc2626', '#ea580c', '#ca8a04'];

  // Get unique values for filter options
  const uniqueScopes = [...new Set(rawData.carbonData.map(item => item.scope).filter(Boolean))];
  const uniqueCategories = [...new Set(rawData.esgData.map(item => item.category).filter(Boolean))];
  const uniqueCalculationTypes = [...new Set(rawData.carbonData.map(item => item.calculation_type).filter(Boolean))];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <div className="animate-pulse bg-gray-300 h-8 w-32 rounded"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-64 bg-gray-300 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-gray-600">Interactive charts based on your organization's data</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchUserData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Chart Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label>Time Range</Label>
              <TimeRangeFilter 
                value={filters.timeRange} 
                onChange={(value) => handleFilterChange('timeRange', value)} 
              />
            </div>
            
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={filters.scope} onValueChange={(value) => handleFilterChange('scope', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Scopes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scopes</SelectItem>
                  {uniqueScopes.map(scope => (
                    <SelectItem key={scope} value={scope}>{scope}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={filters.category} onValueChange={(value) => handleFilterChange('category', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {uniqueCategories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Calculation Type</Label>
              <Select value={filters.calculationType} onValueChange={(value) => handleFilterChange('calculationType', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {uniqueCalculationTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <TagFilterComponent
                selectedTags={filters.selectedTags}
                onTagsChange={(tags) => handleFilterChange('selectedTags', tags)}
                dataType="all"
                showCategoryFilter={false}
              />
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <Input
                placeholder="Search activities..."
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Emissions by Scope */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Carbon Emissions by Scope</CardTitle>
            <p className="text-sm text-gray-600">
              Total: {chartData.emissions.reduce((sum, item) => sum + item.value, 0).toLocaleString()} tCO2e
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.emissions}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value: any, name: any) => [`${value} tCO2e`, 'Emissions']}
                  labelFormatter={(label) => `Scope: ${label}`}
                />
                <Bar 
                  dataKey="value" 
                  fill="#059669"
                  onClick={(data) => handleChartClick(data, {
                    title: 'Carbon Emissions by Scope',
                    category: 'emissions',
                    data: chartData.emissions
                  })}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Energy Consumption */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Energy Consumption</CardTitle>
            <p className="text-sm text-gray-600">
              Total: {chartData.energy.reduce((sum, item) => sum + item.value, 0).toLocaleString()} kWh
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData.energy}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  onClick={(data) => handleChartClick({ payload: data }, {
                    title: 'Energy Consumption',
                    category: 'energy',
                    data: chartData.energy
                  })}
                  style={{ cursor: 'pointer' }}
                >
                  {chartData.energy.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: any) => [`${value} kWh`, 'Energy']}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* ESG Scores */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">ESG Performance Scores</CardTitle>
            <p className="text-sm text-gray-600">
              Average: {chartData.esgScores.length > 0 ? Math.round(chartData.esgScores.reduce((sum, item) => sum + item.value, 0) / chartData.esgScores.length) : 0}
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData.esgScores}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value: any) => [`${value}`, 'Score']}
                  labelFormatter={(label) => `Category: ${label}`}
                />
                <Bar 
                  dataKey="value" 
                  fill="#0891b2"
                  onClick={(data) => handleChartClick(data, {
                    title: 'ESG Performance Scores',
                    category: 'esg',
                    data: chartData.esgScores
                  })}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Trends */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="text-lg">Monthly Emissions Trend</CardTitle>
            <p className="text-sm text-gray-600">
              Trend over time
            </p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData.monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value: any) => [`${value} tCO2e`, 'Emissions']}
                  labelFormatter={(label) => `Period: ${label}`}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#7c3aed" 
                  strokeWidth={3}
                  dot={{ 
                    fill: '#7c3aed', 
                    strokeWidth: 2, 
                    r: 6,
                    cursor: 'pointer',
                    onClick: (data: any) => handleChartClick({ payload: data.payload }, {
                      title: 'Monthly Emissions Trend',
                      category: 'trends',
                      data: chartData.monthlyTrends
                    })
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {selectedChart && (
        <ChartDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedChart.title}
          data={selectedChart.data}
          category={selectedChart.category}
          clickedValue={selectedChart.clickedValue}
          clickedLabel={selectedChart.clickedLabel}
        />
      )}
    </div>
  );
};
